import { useState, useEffect } from "react";
import "./App.css";

const COLORS = {
  bg: "#0B0F1A",
  surface: "#131929",
  surfaceHover: "#1A2235",
  border: "#1E2D47",
  accent: "#7C6FF7",
  accentDim: "#2D2860",
  accentLight: "#A89FF9",
  text: "#E8EAF0",
  muted: "#6B7A99",
  success: "#3DD68C",
};

const ROLES = [
  "Software Engineer",
  "Python & Django Developer",
  "Node.js Backend Engineer",
  "React.js Frontend Dev",
  "PostgreSQL Database Designer",
];

const SKILLS = [
  { title: "Backend",       chips: ["Python", "Django", "Django REST", "Node.js", "Express.js"] },
  { title: "Frontend",      chips: ["React.js", "JavaScript ES6+", "HTML5", "CSS3", "Redux"] },
  { title: "Database",      chips: ["PostgreSQL", "MongoDB", "Redis", "SQL"] },
  { title: "DevOps & Tools",chips: ["Git", "Docker", "Linux", "Nginx", "REST APIs"] },
];

const PROJECTS = [
  {
    name: "E-Commerce Platform",
    emoji: "🛒",
    desc: "Scalable storefront with product management, cart, order tracking, and Stripe payments. Django REST backend with a React storefront.",
    tags: ["Python", "Django", "React.js", "PostgreSQL", "Stripe"],
    status: "Live",
  },
  {
    name: "Analytics Dashboard",
    emoji: "📊",
    desc: "Real-time KPI dashboard powered by Node.js WebSockets for live data ingestion, PostgreSQL for metric storage, and React charts.",
    tags: ["Node.js", "Express", "React.js", "PostgreSQL", "WebSockets"],
    status: "Live",
  },
  {
    name: "Task Management API",
    emoji: "✅",
    desc: "RESTful API with role-based access control, JWT auth, email notifications via Celery, and detailed activity logging.",
    tags: ["Django REST", "PostgreSQL", "JWT", "Celery", "Redis"],
    status: "Live",
  },
  {
    name: "Real-time Chat App",
    emoji: "💬",
    desc: "Messaging app with rooms, DMs, and file sharing. Built on Socket.io with a snappy React UI.",
    tags: ["Node.js", "Socket.io", "React.js", "MongoDB"],
    status: "Live",
  },
];

const CONTACT = [
  { icon: "✉️", label: "Email",    val: "shahriaralamsakib7039@gmail.com" },
  { icon: "🐙", label: "GitHub",   val: "github.com/shahriaralamsakib" },
  { icon: "💼", label: "LinkedIn", val: "linkedin.com/in/shahriaralamsakib" },
  { icon: "📍", label: "Location", val: "Open to Remote" },
];

// ── Typewriter hook ──────────────────────────────────────────────────────────
function useTypewriter(words, speed = 70, pause = 1800) {
  const [display, setDisplay]   = useState("");
  const [wordIdx, setWordIdx]   = useState(0);
  const [charIdx, setCharIdx]   = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const word = words[wordIdx];
    let timeout;

    if (!deleting && charIdx < word.length) {
      timeout = setTimeout(() => setCharIdx((c) => c + 1), speed);
    } else if (!deleting && charIdx === word.length) {
      timeout = setTimeout(() => setDeleting(true), pause);
    } else if (deleting && charIdx > 0) {
      timeout = setTimeout(() => setCharIdx((c) => c - 1), speed / 2);
    } else if (deleting && charIdx === 0) {
      setDeleting(false);
      setWordIdx((i) => (i + 1) % words.length);
    }

    setDisplay(word.slice(0, charIdx));
    return () => clearTimeout(timeout);
  }, [charIdx, deleting, wordIdx, words, speed, pause]);

  return display;
}

// ── Nav link ─────────────────────────────────────────────────────────────────
function NavLink({ label, target, active, onClick }) {
  return (
    <li>
      <a
        href={`#${target}`}
        onClick={onClick}
        style={{
          fontSize: "13px",
          color: active ? COLORS.accent : COLORS.muted,
          letterSpacing: "0.03em",
          transition: "color 0.2s",
          fontWeight: active ? 500 : 400,
        }}
      >
        {label}
      </a>
    </li>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function App() {
  const typed = useTypewriter(ROLES);
  const [activeSection, setActiveSection] = useState("home");
  const [hoveredProj,   setHoveredProj]   = useState(null);
  const [hoveredSkill,  setHoveredSkill]  = useState(null);
  const [blink,         setBlink]         = useState(true);
  const [menuOpen,      setMenuOpen]      = useState(false);

  // Blinking cursor
  useEffect(() => {
    const id = setInterval(() => setBlink((b) => !b), 530);
    return () => clearInterval(id);
  }, []);

  // Active section via IntersectionObserver
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActiveSection(e.target.id);
        });
      },
      { rootMargin: "-40% 0px -50% 0px" }
    );
    ["home", "skills", "projects", "contact"].forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  const NAV_ITEMS = [
    ["Home", "home"],
    ["Skills", "skills"],
    ["Projects", "projects"],
    ["Contact", "contact"],
  ];

  return (
    <div className="app">
      {/* ── Nav ── */}
      <nav className="nav">
        <span className="logo">sakib.dev</span>

        {/* Desktop links */}
        <ul className="nav-links">
          {NAV_ITEMS.map(([label, id]) => (
            <NavLink
              key={id}
              label={label}
              target={id}
              active={activeSection === id}
              onClick={(e) => { e.preventDefault(); scrollTo(id); }}
            />
          ))}
        </ul>

        {/* Mobile hamburger */}
        <button className="hamburger" onClick={() => setMenuOpen((o) => !o)} aria-label="Toggle menu">
          <span /><span /><span />
        </button>
      </nav>

      {/* Mobile drawer */}
      {menuOpen && (
        <div className="mobile-menu">
          {NAV_ITEMS.map(([label, id]) => (
            <button key={id} onClick={() => scrollTo(id)} className="mobile-link">
              {label}
            </button>
          ))}
        </div>
      )}

      <main style={{ paddingTop: "60px" }}>

        {/* ── Hero ── */}
        <section id="home" className="section hero-section">
          <div className="eyebrow">
            <span className="status-dot" />
            Available for hire
          </div>

          <div className="hero-heading">
            <h1 className="h1">
              Md. Shahriar<br />Alam Sakib
            </h1>

            <div className="hero-profile">
              <img
                src="/profile.jpg"
                alt="Md. Shahriar Alam Sakib"
                className="hero-profile-img"
              />
            </div>
          </div>

          <div className="terminal">
            <span className="prompt">&gt;</span>
            {typed}
            <span className="cursor" style={{ opacity: blink ? 1 : 0 }} />
          </div>

          <p className="hero-bio">
            Software engineer specialising in full-stack web development — building scalable backends
            with Python and Django, performant APIs with Node.js, dynamic UIs with React, and robust
            data architectures with PostgreSQL.
          </p>

          <div className="btn-row">
            <button className="btn-primary" onClick={() => scrollTo("projects")}>
              View projects →
            </button>
            <button className="btn-ghost" onClick={() => scrollTo("contact")}>
              Get in touch
            </button>
          </div>
        </section>

        {/* ── Skills ── */}
        <section id="skills" className="section">
          <div className="section-label">02 — Skills</div>
          <h2 className="h2">Tech stack</h2>
          <div className="divider" />

          <div className="skill-grid">
            {SKILLS.map((group, i) => (
              <div
                key={i}
                className={`skill-card ${hoveredSkill === i ? "skill-card--hovered" : ""}`}
                onMouseEnter={() => setHoveredSkill(i)}
                onMouseLeave={() => setHoveredSkill(null)}
              >
                <div className="skill-card-title">{group.title}</div>
                <div className="skill-chips">
                  {group.chips.map((c) => (
                    <span key={c} className="chip">{c}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Projects ── */}
        <section id="projects" className="section">
          <div className="section-label">03 — Projects</div>
          <h2 className="h2">Selected work</h2>
          <div className="divider" />

          <div className="proj-grid">
            {PROJECTS.map((p, i) => (
              <div
                key={i}
                className={`proj-card ${hoveredProj === i ? "proj-card--hovered" : ""}`}
                onMouseEnter={() => setHoveredProj(i)}
                onMouseLeave={() => setHoveredProj(null)}
              >
                <div className="proj-left">
                  <div className="proj-name">
                    <span>{p.emoji}</span> {p.name}
                  </div>
                  <p className="proj-desc">{p.desc}</p>
                  <div className="proj-tags">
                    {p.tags.map((t) => (
                      <span key={t} className="proj-tag">{t}</span>
                    ))}
                  </div>
                </div>
                <div className="proj-status">
                  <span className="status-dot" />
                  <span className="status-text">{p.status}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Contact ── */}
        <section id="contact" className="section">
          <div className="section-label">04 — Contact</div>
          <h2 className="h2">Let's work together</h2>
          <div className="divider" />

          <p className="hero-bio" style={{ marginBottom: "2rem" }}>
            Open to new opportunities, freelance projects, and interesting collaborations.
            Drop me a message and I'll get back to you promptly.
          </p>

          <div className="contact-card">
            {CONTACT.map((c) => (
              <div key={c.label} className="contact-item">
                <div className="contact-icon">{c.icon}</div>
                <div>
                  <div className="contact-label">{c.label}</div>
                  <div className="contact-val">{c.val}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>

      <footer className="footer">
        <span style={{ color: COLORS.accent }}>sakib.dev</span>
        {" "}— built with React.js · {new Date().getFullYear()}
      </footer>
    </div>
  );
}
